/**
 * 🔄 Retry Utility for Transient Infrastructure Errors (e.g., Supabase / PostgREST PGRST002 & 5xx HTTP Statuses)
 */

export interface RetryOptions {
  maxRetries?: number
  delayMs?: number
}

export interface TransientError {
  code?: string
  message?: string
  status?: number
  statusCode?: number
}

export interface PostgrestLikeResponse<T> {
  data: T | null
  error: TransientError | null
  status?: number
  statusText?: string
}

const TRANSIENT_STATUSES = [408, 429, 502, 503, 504]
// PGRST303 = "JWT issued at future": kimlik damgası sunucudan gelir; iki sunucu saati
// arasındaki birkaç saniyelik fark yüzünden taze belirteç "gelecekten" görünebilir.
// Birkaç saniye sonra aynı istek geçer (Sentry GUNUM-VAR-1K, 31 Ağustos 2026).
const TRANSIENT_CODES = ['PGRST002', 'PGRST303']

/**
 * Checks if a Supabase or network response error / status is considered a transient infrastructure error.
 */
export function isTransientError(error: TransientError | null, status?: number): boolean {
  if (status && TRANSIENT_STATUSES.includes(status)) {
    return true
  }

  if (!error) return false

  const errStatus = error.status || error.statusCode
  if (errStatus && TRANSIENT_STATUSES.includes(errStatus)) {
    return true
  }

  const code = String(error.code || '')
  if (TRANSIENT_CODES.includes(code)) {
    return true
  }

  const msg = String(error.message || '').toLowerCase()
  if (msg.includes('schema cache') || msg.includes('connection pool')) {
    return true
  }

  return false
}

/**
 * Executes an async operation returning `{ data, error, status }`.
 * Retries automatically up to `maxRetries` times if the error or HTTP status is transient.
 *
 * ⚠️ YALNIZCA idempotent/okuma (SELECT) işlemleri için kullanın.
 * INSERT/UPDATE/DELETE gibi yazma işlemlerinde idempotency anahtarı olmadan KULLANMAYIN — çift-yazma riski.
 */
export async function withTransientRetry<T>(
  fn: () => Promise<PostgrestLikeResponse<T>>,
  options: RetryOptions = {}
): Promise<PostgrestLikeResponse<T>> {
  const maxRetries = options.maxRetries ?? 2
  const delayMs = options.delayMs ?? 150

  let attempt = 0
  while (true) {
    const res = await fn()

    if (!res.error || !isTransientError(res.error, res.status) || attempt >= maxRetries) {
      return res
    }

    attempt++
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }
}
